import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

const languages = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'da', label: 'Dansk', flag: '🇩🇰' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCategoryDropdownOpen(false);
      }
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setLangDropdownOpen(false);
  };

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50 border-b border-background-200/70">
      <div className="container-main">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 flex-shrink-0">
            <span className="font-heading text-lg md:text-xl font-bold text-foreground-950">
              AllChinaBuy<span className="text-primary-600">Spread</span>
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            <a href="/" className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap">
              {t('nav.home')}
            </a>

            {/* Category Dropdown */}
            <div className="relative" ref={catRef}>
              <button
                onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
                className="flex items-center gap-1 text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                {t('nav.categories')}
                <i className={`ri-arrow-down-s-line transition-transform ${categoryDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {categoryDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-lg border border-background-200/70 overflow-hidden shadow-lg max-h-96 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                      onClick={() => setCategoryDropdownOpen(false)}
                    >
                      <div className="w-9 h-9 rounded-md overflow-hidden flex-shrink-0">
                        <img src={cat.image} alt="" className="w-full h-full object-cover" />
                      </div>
                      <span className="font-medium">{t(cat.i18nKey)}</span>
                      <i className="ri-arrow-right-up-line ml-auto text-foreground-300 text-xs" />
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a href="/allchinabuy-spreadsheet-complete-guide" className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap">
              {t('nav.guide')}
            </a>
            <a href="/safety" className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap">
              {t('nav.safety')}
            </a>
            <a href="/about" className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap">
              {t('nav.about')}
            </a>
            <a href="/contact" className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap">
              {t('nav.contact')}
            </a>
          </div>

          {/* Right side: Language + CTA */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Language Switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors px-3 py-2 rounded-md border border-background-200"
              >
                <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs">{currentLang.flag}</span>
                <span className="uppercase">{currentLang.code}</span>
                <i className={`ri-arrow-down-s-line transition-transform ${langDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {langDropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-40 bg-background-50 rounded-lg border border-background-200/70 overflow-hidden shadow-lg">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`flex items-center gap-2 w-full px-4 py-2.5 text-sm text-left transition-colors ${
                        i18n.language === lang.code ? 'bg-primary-50 text-primary-700' : 'text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs">{lang.flag}</span>
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="btn-primary text-xs whitespace-nowrap"
            >
              {t('cta.main_button')}
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 text-foreground-900 flex items-center justify-center w-10 h-10"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menu"
          >
            <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-2xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200/70 max-h-[85vh] overflow-y-auto">
          <div className="container-main py-4 space-y-4">
            {/* Primary links */}
            <div className="space-y-1">
              <a href="/" className="block px-3 py-2.5 text-sm font-semibold text-foreground-900 rounded-md hover:bg-background-100">
                {t('nav.home')}
              </a>
              <a href="/allchinabuy-spreadsheet-complete-guide" className="block px-3 py-2.5 text-sm font-semibold text-foreground-900 rounded-md hover:bg-background-100">
                {t('nav.guide')}
              </a>
              <a href="/safety" className="block px-3 py-2.5 text-sm font-semibold text-foreground-900 rounded-md hover:bg-background-100">
                {t('nav.safety')}
              </a>
              <a href="/about" className="block px-3 py-2.5 text-sm font-semibold text-foreground-900 rounded-md hover:bg-background-100">
                {t('nav.about')}
              </a>
              <a href="/contact" className="block px-3 py-2.5 text-sm font-semibold text-foreground-900 rounded-md hover:bg-background-100">
                {t('nav.contact')}
              </a>
            </div>

            {/* Categories grid with images */}
            <div className="border-t border-background-200/70 pt-4">
              <p className="text-xs font-semibold text-foreground-400 mb-3 uppercase tracking-wider">{t('nav.categories')}</p>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2 p-2 rounded-lg border border-background-200/70 hover:border-primary-300 hover:bg-background-100 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-md overflow-hidden flex-shrink-0">
                      <img src={cat.image} alt="" className="w-full h-full object-cover" />
                    </div>
                    <span className="text-xs font-medium text-foreground-700 leading-tight">{t(cat.i18nKey)}</span>
                  </a>
                ))}
              </div>
            </div>

            {/* Language */}
            <div className="border-t border-background-200/70 pt-4">
              <p className="text-xs font-semibold text-foreground-400 mb-3 uppercase tracking-wider">Language</p>
              <div className="flex flex-wrap gap-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => { changeLanguage(lang.code); setMobileMenuOpen(false); }}
                    className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                      i18n.language === lang.code
                        ? 'border-primary-300 bg-primary-50 text-primary-700'
                        : 'border-background-200 text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="border-t border-background-200/70 pt-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="btn-primary w-full text-sm justify-center whitespace-nowrap"
              >
                {t('cta.main_button')}
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}